import React from 'react';
import { useFeatureFlag } from '@moda/portal-stanchions';
import {
  useRecentlyViewed,
  useFavoritedProducts,
  useSimilarProducts,
  useTopTrunkshows,
  useLookVariants
} from '../../hooks/useRecommendations';
import { EnableLazyLoad } from '../LazyLoad';
import { DividerModuleProps } from '../DividerPumoModule';
import { ProductDetailPageFragment } from '../../generated/types/ProductDetailPageFragment';
import { RecommendationsCarousel } from './RecommendationsCarousel';

export const ALGORITHMS: { [key: string]: string } = {
  RECENTLY_VIEWED: 'recently viewed',
  TOP_TRUNKSHOWS: 'top trunkshows',
  FAVORITES: 'favorites',
  SIMILAR_PRODUCTS: 'similar products',
  LOOK_VARIANTS: 'look variants'
};

interface RecommendationProps {
  divider: DividerModuleProps;
  variantId?: string;
  identifier: string;
  variant?: ProductDetailPageFragment;
  lookImageUrl?: string | null;
  variantPrice?: string | number | null;
}

export const RecentlyViewed: React.FC<RecommendationProps> = ({ divider, identifier }) => {
  const { loading, error, variants } = useRecentlyViewed();
  return (
    <RecommendationsCarousel
      identifier={`RecentlyViewed-${identifier}`}
      loading={loading}
      error={error}
      divider={divider}
      cells={variants}
    />
  );
};

export const Favorites: React.FC<RecommendationProps> = ({ divider, identifier }) => {
  const { loading, error, variants } = useFavoritedProducts();
  return (
    <RecommendationsCarousel
      identifier={`Favorites-${identifier}`}
      loading={loading}
      error={error}
      divider={divider}
      cells={variants}
    />
  );
};

export const SimilarProducts: React.FC<RecommendationProps> = ({
  divider,
  variantPrice,
  variantId,
  identifier
}) => {
  const { loading, error, variants } = useSimilarProducts(variantId, variantPrice);

  return (
    <RecommendationsCarousel
      identifier={`SimilarProducts-${identifier}`}
      loading={loading}
      error={error}
      divider={divider}
      cells={variants}
    />
  );
};

export const TopTrunkshows: React.FC<RecommendationProps> = ({ divider, identifier }) => {
  const { loading, error, trunkshows } = useTopTrunkshows();
  return (
    <RecommendationsCarousel
      identifier={`TopTrunkshows-${identifier}`}
      loading={loading}
      error={error}
      divider={divider}
      cells={trunkshows}
    />
  );
};

export const LookVariants: React.FC<RecommendationProps> = ({
  divider,
  variantId,
  identifier,
  variant,
  lookImageUrl
}) => {
  const { loading, error, variants } = useLookVariants(variantId);
  const howToWearItLargeDisplayFlag = useFeatureFlag('how-to-wear-it-large-display', [
    { default: false },
    { if: true, then: true }
  ]);

  return (
    <RecommendationsCarousel
      error={error}
      cells={variants}
      loading={loading}
      divider={divider}
      variant={variant}
      lookImageUrl={lookImageUrl}
      identifier={`TopTrunkshows-${identifier}`}
      howToWearItLargeDisplayFlag={howToWearItLargeDisplayFlag}
    />
  );
};

const ALGORITHM_COMPONENT_MAP = {
  [ALGORITHMS.RECENTLY_VIEWED]: RecentlyViewed,
  [ALGORITHMS.TOP_TRUNKSHOWS]: TopTrunkshows,
  [ALGORITHMS.FAVORITES]: Favorites,
  [ALGORITHMS.SIMILAR_PRODUCTS]: SimilarProducts,
  [ALGORITHMS.LOOK_VARIANTS]: LookVariants
};

interface Props {
  algorithm: string | null;
  variantId?: string;
  divider: DividerModuleProps;
  identifier: string;
  variant?: ProductDetailPageFragment;
  lookImageUrl?: string | null;
  variantPrice?: string | number | null;
}

export const RecommendationsModule: React.FC<Props> = ({
  algorithm,
  variantId,
  divider,
  identifier,
  variant,
  lookImageUrl,
  variantPrice
}) => {
  if (!algorithm) return null;

  const RecommendationComponent = ALGORITHM_COMPONENT_MAP[algorithm];

  if (!RecommendationComponent) return null;

  return (
    <EnableLazyLoad>
      <RecommendationComponent
        divider={divider}
        variantId={variantId}
        identifier={identifier}
        variant={variant}
        lookImageUrl={lookImageUrl}
        variantPrice={variantPrice}
      />
    </EnableLazyLoad>
  );
};
